import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Section = ({ heading, children }) => (
  <section className="space-y-2">
    {heading && (
      <h2 className="text-base font-semibold text-foreground">{heading}</h2>
    )}
    <div className="text-sm text-muted-foreground leading-relaxed space-y-2">
      {children}
    </div>
  </section>
);

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-4xl mx-auto flex h-14 items-center px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="mr-2"
            data-testid="terms-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">Terms of Service</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="p-6 sm:p-8" data-testid="terms-content">
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Terms of Service
              </h1>
              <p className="text-sm text-muted-foreground">
                Foresight Meet Schedule Predictor
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Effective Date: April 17, 2026
              </p>
              <p className="text-xs text-muted-foreground">
                Last Updated: July 4, 2026
              </p>
            </div>

            <Section heading="1. Acceptance of Terms">
              <p>
                By downloading, installing, purchasing, or using Foresight Meet Schedule
                Predictor (the &ldquo;App&rdquo;), you agree to be bound by these Terms of
                Service (&ldquo;Terms&rdquo;). If you do not agree to these Terms, please
                do not use or purchase the App.
              </p>
            </Section>

            <Section heading="2. About Us">
              <p>
                The App is developed and operated by Foresight Pulse LLC
                (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;), a limited
                liability company based in Dublin, Ohio, United States.
              </p>
            </Section>

            <Section heading="3. License">
              <p>
                We grant you a limited, non-exclusive, non-transferable, and revocable
                license to download and use the App solely for your personal,
                non-commercial use on a device that you own or control. This license is
                subject to these Terms and the rules of the Apple App Store or Google Play
                Store.
              </p>
            </Section>

            <Section heading="4. Account Registration and Authentication">
              <p>
                To use certain features of the App, such as syncing data across devices,
                you may be required to sign in using a third-party authentication provider
                (such as Google Sign-In). You agree to provide accurate profile information
                if prompted and are responsible for maintaining the security of your
                authentication credentials.
              </p>
            </Section>

            <Section heading="5. In-App Purchases and Payments">
              <p>
                The App uses a freemium model: you can create a limited number of meets for
                free, after which you may purchase a one-time upgrade for unlimited meets.
                Pricing is clearly displayed in the Apple App Store or Google Play Store.
              </p>
              <p>
                All payments are processed exclusively by Apple (via the App Store) or
                Google (via Google Play). We do not collect, store, or handle your payment
                information.
              </p>
              <p>
                Purchases are final. Refunds are handled solely by Apple or Google according
                to their refund policies. You are responsible for any applicable taxes.
              </p>
            </Section>

            <Section heading="6. Acceptable Use and Restrictions">
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  Copy, modify, reverse engineer, decompile, disassemble, or create
                  derivative works of the App
                </li>
                <li>Distribute, sell, rent, lease, or sublicense the App or any part of it</li>
                <li>Use the App for any commercial purpose or illegal activity</li>
                <li>Circumvent or attempt to bypass any payment or usage limits</li>
                <li>Remove or alter any copyright, trademark, or proprietary notices</li>
                <li>Use the App in any way that violates applicable laws or these Terms</li>
              </ul>
            </Section>

            <Section heading="7. No Professional Advice / Accuracy Disclaimer">
              <p>
                The App provides tools for creating sports meet schedules (including
                track &amp; field, cross country, and swimming) and related estimates for
                informational and organizational purposes only. All data, predictions, and
                schedules are generated based on user input and algorithms.
              </p>
              <p>
                We do not guarantee the accuracy, completeness, or reliability of any
                information in the App. The App is not professional coaching, official
                timing, betting, or financial advice. You use the App entirely at your own
                risk.
              </p>
            </Section>

            <Section heading="8. User Content and Data Storage">
              <p>
                Your created meet schedules, settings, and account identifiers are stored
                on our secure cloud servers to enable real-time syncing and data backup. By
                using the App, you consent to this storage. For details on how we handle
                your data, please see our Privacy Policy.
              </p>
            </Section>

            <Section heading="9. Intellectual Property">
              <p>
                All content, code, designs, graphics, trademarks, and materials in the App
                are owned by Foresight Pulse LLC or our licensors and are protected by
                copyright and trademark laws. You may not use them without our prior
                written permission, except as expressly allowed under this license.
              </p>
            </Section>

            <Section heading="10. Disclaimers and No Warranty">
              <p>
                The App is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;
                without any warranties of any kind, express or implied, including but not
                limited to warranties of merchantability, fitness for a particular purpose,
                or non-infringement.
              </p>
            </Section>

            <Section heading="11. Limitation of Liability">
              <p>
                To the maximum extent permitted by law, Foresight Pulse LLC shall not be
                liable for any indirect, incidental, special, consequential, or punitive
                damages arising out of or related to your use of the App, even if we have
                been advised of the possibility of such damages.
              </p>
              <p>
                Our total liability to you for any claim shall not exceed the amount you
                actually paid for the App or relevant in-app purchase (if any) in the
                twelve (12) months preceding the claim.
              </p>
            </Section>

            <Section heading="12. Termination">
              <p>
                We may suspend or terminate your access to the App at any time, with or
                without notice, for any reason, including violation of these Terms.
              </p>
            </Section>

            <Section heading="13. Changes to These Terms">
              <p>
                We may update these Terms from time to time. We will notify you of material
                changes by posting the updated Terms in the App and updating the
                &ldquo;Last Updated&rdquo; date. Your continued use of the App after such
                changes constitutes your acceptance of the new Terms.
              </p>
            </Section>

            <Section heading="14. Governing Law">
              <p>
                These Terms are governed by the laws of the State of Ohio, United States,
                without regard to its conflict of law principles. Any disputes arising from
                these Terms or your use of the App shall be resolved exclusively in the
                state or federal courts located in Franklin County, Ohio.
              </p>
            </Section>

            <Section heading="15. Apple-Specific Terms">
              <p>
                These Terms supplement (and do not replace) the Apple App Store Terms of
                Service. Apple is a third-party beneficiary of these Terms and may enforce
                them against you.
              </p>
            </Section>

            <Section heading="16. Entire Agreement">
              <p>
                These Terms, together with our Privacy Policy, constitute the entire
                agreement between you and Foresight Pulse LLC regarding your use of the
                App.
              </p>
            </Section>

            <Section heading="Contact Us">
              <p>
                If you have any questions about these Terms of Service, please contact us
                at:
              </p>
              <p>
                Foresight Pulse LLC
                <br />
                Dublin, Ohio, United States
                <br />
                Email:{' '}
                <a
                  href="mailto:foresightpulse@gmail.com"
                  className="underline hover:text-foreground"
                >
                  foresightpulse@gmail.com
                </a>
              </p>
            </Section>
          </div>
        </Card>
      </main>
    </div>
  );
}
